import classnames from 'classnames';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { PositionObserver } from '../utils';

import styles from './StyledTable.module.scss';
import { StyledTableProps } from './StyledTable.types';

export const StyledTable = forwardRef<HTMLDivElement, StyledTableProps>(
  (
    {
      containerClassName,
      color = 'white',
      size,
      stickyHeader,
      stickyHeaderDistance,
      className,
      ...props
    },
    ref
  ) => {
    const innerRef = useRef<HTMLDivElement | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useImperativeHandle(ref, () => innerRef.current!);

    const [verticalScrolling, setVerticalScrolling] = useState(false);
    const [horizontalScrolling, setHorizontalScrolling] = useState(false);
    const checkScrolling = useCallback(() => {
      if (!innerRef.current?.parentElement) {
        setVerticalScrolling(false);
        setHorizontalScrolling(false);
      } else {
        setVerticalScrolling(
          innerRef.current.parentElement.clientHeight !==
            innerRef.current.parentElement.scrollHeight
        );
        setHorizontalScrolling(
          innerRef.current.parentElement.clientWidth !== innerRef.current.parentElement.scrollWidth
        );
      }
    }, []);
    useLayoutEffect(() => checkScrolling());
    useEffect(() => {
      if (!innerRef.current) return;
      const ob = new PositionObserver(checkScrolling, {});
      ob.observe(innerRef.current);
      return () => ob.disconnect();
    }, [checkScrolling]);

    return (
      <div
        className={classnames(styles.container, containerClassName, styles[size])}
        data-table-size={size}
        data-row-color={color}
        data-sticky-header={stickyHeader ? true : undefined}
        data-sticky-header-distance={stickyHeaderDistance}
        data-vertical-scrolling={verticalScrolling ? true : undefined}
        data-horizontal-scrolling={horizontalScrolling ? true : undefined}
      >
        <div
          ref={innerRef}
          className={classnames(styles.table, className)}
          data-role="table"
          {...props}
        />
      </div>
    );
  }
);
